import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

import { AuthContext } from "../context/AuthContext";
import StandardLayout, {
  EventsContext,
} from "../layouts/StandardLayout";
import GuestLayout from "../layouts/GuestLayout";
import Events from "../pages/Events";
import FirstReservationIntro from "../components/FirstReservationIntro";

const DESKTOP_LANDING_BREAKPOINT = 440;

function isTrueMobileDevice() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const ua = navigator.userAgent.toLowerCase();
  return /android|iphone|ipad|ipod|mobile|tablet/i.test(ua);
}

function getIsDesktopLandingWidth() {
  if (typeof window === "undefined") {
    return false;
  }

  return (
    window.innerWidth > DESKTOP_LANDING_BREAKPOINT &&
    !isTrueMobileDevice()
  );
}

/*
 * This component mounts inside StandardLayout or GuestLayout,
 * where the selected market is available through EventsContext.
 */
function MarketEventsContent({
  membership,
  currentUser,
  accessToken,
  isDesktopLandingWidth,
  reservationIntroBlocking,
  onBlockingChange,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const { selectedMarketId = "" } =
    useContext(EventsContext) || {};

  const [filterOverlayOpen, setFilterOverlayOpen] = useState(false);

  const [recommendation, setRecommendation] = useState(null);
  const requestVersionRef = useRef(0);

  const userId = String(
    currentUser?._id ||
      localStorage.getItem("userId") ||
      ""
  );

  const canFetchRecommendation = Boolean(
    accessToken &&
      userId &&
      selectedMarketId &&
      !isDesktopLandingWidth
  );

  useEffect(() => {
    let active = true;
    let controller = null;

    setRecommendation(null);

    if (!canFetchRecommendation) {
      return undefined;
    }

    const fetchRecommendedEvent = async () => {
      controller?.abort();
      controller = new AbortController();

      const signal = controller.signal;
      const version = ++requestVersionRef.current;

      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_URL}/events/recommended`,
          {
            params: {
              marketId: selectedMarketId,
            },
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            signal,
          }
        );

        if (
          !active ||
          signal.aborted ||
          version !== requestVersionRef.current
        ) {
          return;
        }

        setRecommendation({
          marketId: selectedMarketId,
          userId,
          accessToken,
          event: data?.event || null,
          isReserved: data?.isReserved === true,
          weekKey: data?.weekKey || null,
        });
      } catch (err) {
        if (
          !active ||
          signal.aborted ||
          version !== requestVersionRef.current
        ) {
          return;
        }

        console.warn(
          "[recommended event fetch]",
          err?.response?.data || err.message
        );

        setRecommendation(null);
      }
    };

    const refresh = () => {
      if (document.visibilityState === "visible") {
        fetchRecommendedEvent();
      }
    };

    fetchRecommendedEvent();

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    const interval = window.setInterval(refresh, 60 * 1000);

    return () => {
      active = false;
      controller?.abort();

      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(interval);
    };
  }, [
    canFetchRecommendation,
    selectedMarketId,
    userId,
    accessToken,
    location.key,
  ]);

  /*
   * Hide the previous recommendation immediately on a market
   * or account change, before effects start the next request.
   */
  const currentRecommendation =
    canFetchRecommendation &&
    recommendation?.marketId === selectedMarketId &&
    recommendation?.userId === userId &&
    recommendation?.accessToken === accessToken
      ? recommendation
      : null;

  const recommendedEvent = currentRecommendation?.event || null;

  useEffect(() => {
    if (!accessToken || isDesktopLandingWidth) {
      onBlockingChange(false);
    }
  }, [
    accessToken,
    isDesktopLandingWidth,
    onBlockingChange,
  ]);

  return (
    <>
      {!isDesktopLandingWidth && accessToken && (
        <div
          style={{
            display: filterOverlayOpen ? "none" : undefined,
          }}
        >
          <FirstReservationIntro
            membership={membership}
            recommendedGathering={recommendedEvent}
            hasUpcomingReservation={
              membership?.hasUpcomingReservation === true ||
              currentRecommendation?.isReserved === true
            }
            userFirstName={currentUser?.firstName || ""}
            firstReservationAt={
              currentUser?.firstReservationAt || null
            }
            onBlockingChange={onBlockingChange}
            onReserveRecommended={(gathering) => {
              const gatheringId = gathering?._id || gathering?.id;

              if (!gatheringId) {
                return;
              }

              navigate(`/events/${gatheringId}`, {
                state: {
                  gathering,
                  openReservationSheet: true,
                },
              });
            }}
            onExplore={() => {
              document
                .getElementById("events-list")
                ?.scrollIntoView({
                  behavior: "smooth",
                  block: "start",
                });
            }}
          />
        </div>
      )}

      <Events
        membership={membership}
        recommendedEvent={recommendedEvent}
        reservationIntroBlocking={reservationIntroBlocking}
        onFilterOverlayChange={setFilterOverlayOpen}
      />
    </>
  );
}

export default function ConditionalEventsLayout() {
  const { currentUser } = useContext(AuthContext);
  const location = useLocation();

  const [isDesktopLandingWidth, setIsDesktopLandingWidth] =
    useState(getIsDesktopLandingWidth);

  const [membership, setMembership] = useState(null);
  const [membershipLoaded, setMembershipLoaded] = useState(false);

  const membershipRequestRef = useRef(0);

  const accessToken =
    typeof window !== "undefined"
      ? localStorage.getItem("accessToken")
      : null;

  const [reservationIntroBlocking, setReservationIntroBlocking] =
    useState(() =>
      Boolean(accessToken && !getIsDesktopLandingWidth())
    );

  /*
   * Use the stored token immediately instead of waiting for
   * AuthContext, preserving the existing mobile login behavior.
   */
  const hasAuthenticatedSession = Boolean(
    accessToken || currentUser?._id
  );

  useEffect(() => {
    const handleResize = () => {
      setIsDesktopLandingWidth(getIsDesktopLandingWidth());
    };

    handleResize();

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, []);

  const fetchMembership = useCallback(async () => {
    const version = ++membershipRequestRef.current;

    if (!accessToken) {
      setMembership(null);
      setMembershipLoaded(true);
      return;
    }

    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/subscriptions/me`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );

      if (version === membershipRequestRef.current) {
        setMembership(data || null);
      }
    } catch (err) {
      if (version !== membershipRequestRef.current) {
        return;
      }

      console.warn(
        "[membership fetch]",
        err?.response?.data || err.message
      );

      setMembership(null);
    } finally {
      if (version === membershipRequestRef.current) {
        setMembershipLoaded(true);
      }
    }
  }, [accessToken]);

  useEffect(() => {
    setMembership(null);
    setMembershipLoaded(false);

    return () => {
      membershipRequestRef.current += 1;
    };
  }, [accessToken]);

  useEffect(() => {
    fetchMembership();
  }, [fetchMembership, location.key]);

  useEffect(() => {
    if (!accessToken) {
      return undefined;
    }

    const refresh = () => {
      if (document.visibilityState === "visible") {
        fetchMembership();
      }
    };

    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", refresh);

    const interval = window.setInterval(refresh, 60 * 1000);

    return () => {
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", refresh);
      window.clearInterval(interval);
    };
  }, [accessToken, fetchMembership]);

  const eventsPage = (
    <MarketEventsContent
      membership={membership}
      currentUser={currentUser}
      accessToken={accessToken}
      isDesktopLandingWidth={isDesktopLandingWidth}
      reservationIntroBlocking={reservationIntroBlocking}
      onBlockingChange={setReservationIntroBlocking}
    />
  );

  if (isDesktopLandingWidth) {
    return <GuestLayout>{eventsPage}</GuestLayout>;
  }

  return hasAuthenticatedSession ? (
    <StandardLayout
      deferSmsPrompt={
        Boolean(accessToken) &&
        (!membershipLoaded || reservationIntroBlocking)
      }
    >
      {eventsPage}
    </StandardLayout>
  ) : (
    <GuestLayout>{eventsPage}</GuestLayout>
  );
}